import { StateManager, ConnectionState, StateChangeCallback } from './StateManager';
import { OperationGuard } from './OperationGuard';

import logger from '@/utils/logger';

/**
 * 连接参数
 */
export interface ConnectionParams {
  sceneId?: string;
  autoStart?: boolean;
  question?: string;
  directRoom?: boolean;
}

/**
 * 连接结果
 */
export interface ConnectionResult {
  success: boolean;
  state: ConnectionState;
  error?: Error;
  duration: number;
}

/**
 * 连接管理器配置
 */
export interface ConnectionManagerConfig {
  maxRetries?: number;
  retryDelay?: number;
  connectionTimeout?: number;
}

/**
 * 连接管理器 - 统一管理RTC连接的完整生命周期
 */
export class ConnectionManager {
  private stateManager: StateManager;

  private operationGuard: OperationGuard;

  private config: Required<ConnectionManagerConfig>;

  private isDestroyed = false;

  constructor(config: ConnectionManagerConfig = {}) {
    this.config = {
      maxRetries: 3,
      retryDelay: 1000,
      connectionTimeout: 15000,
      ...config,
    };

    this.stateManager = new StateManager();
    this.operationGuard = new OperationGuard();

    // 监听状态变化进行日志记录
    this.stateManager.onStateChange((event) => {
      logger.info(`[ConnectionManager] 状态变化: ${event.from} -> ${event.to}`, event.context);
    });

    logger.info('[ConnectionManager] 连接管理器已初始化', this.config);
  }

  /**
   * 建立连接
   */
  async connect(params: ConnectionParams = {}): Promise<ConnectionResult> {
    if (this.isDestroyed) {
      throw new Error('ConnectionManager 已销毁');
    }

    const startTime = Date.now();

    return this.operationGuard
      .execute(
        'rtc-connect',
        async () => {
          logger.info('[ConnectionManager] 开始建立连接', params);

          // 状态转换到连接中
          if (!this.stateManager.transition(ConnectionState.CONNECTING, { params })) {
            throw new Error(`无法从 ${this.stateManager.getState()} 状态开始连接`);
          }

          try {
            // 执行实际的连接逻辑
            await this.performConnection(params);

            // 连接成功，转换状态
            this.stateManager.transition(ConnectionState.CONNECTED, { params });

            const duration = Date.now() - startTime;
            logger.info(`[ConnectionManager] 连接建立成功 (耗时: ${duration}ms)`);

            return {
              success: true,
              state: ConnectionState.CONNECTED,
              duration,
            };
          } catch (error) {
            // 连接失败，转换到错误状态
            this.stateManager.transition(ConnectionState.ERROR, { error, params });

            const duration = Date.now() - startTime;
            logger.error(`[ConnectionManager] 连接建立失败 (耗时: ${duration}ms)`, error);

            return {
              success: false,
              state: ConnectionState.ERROR,
              error: error as Error,
              duration,
            };
          }
        },
        1000 // 1秒防抖
      )
      .then((result) => {
        if (result.success && result.data) {
          return result.data;
        }
        throw result.error || new Error('连接操作被阻止');
      });
  }

  /**
   * 断开连接
   */
  async disconnect(): Promise<ConnectionResult> {
    if (this.isDestroyed) {
      throw new Error('ConnectionManager 已销毁');
    }

    const startTime = Date.now();

    return this.operationGuard
      .execute('rtc-disconnect', async () => {
        logger.info('[ConnectionManager] 开始断开连接');

        // 状态转换到断开连接中
        if (!this.stateManager.transition(ConnectionState.DISCONNECTING)) {
          logger.warn(`[ConnectionManager] 从 ${this.stateManager.getState()} 状态断开连接`);
        }

        try {
          // 执行实际的断开逻辑
          await this.performDisconnection();

          // 断开成功，转换状态
          this.stateManager.transition(ConnectionState.IDLE);

          const duration = Date.now() - startTime;
          logger.info(`[ConnectionManager] 连接断开成功 (耗时: ${duration}ms)`);

          return {
            success: true,
            state: ConnectionState.IDLE,
            duration,
          };
        } catch (error) {
          // 断开失败，转换到错误状态
          this.stateManager.transition(ConnectionState.ERROR, { error });

          const duration = Date.now() - startTime;
          logger.error(`[ConnectionManager] 连接断开失败 (耗时: ${duration}ms)`, error);

          return {
            success: false,
            state: ConnectionState.ERROR,
            error: error as Error,
            duration,
          };
        }
      })
      .then((result) => {
        if (result.success && result.data) {
          return result.data;
        }
        throw result.error || new Error('断开操作被阻止');
      });
  }

  /**
   * 执行实际的连接逻辑
   */
  private async performConnection(params: ConnectionParams): Promise<void> {
    // 注意：实际的连接逻辑需要在React组件中通过useJoin Hook执行
    // 这里只是状态管理和资源注册
    logger.info('[ConnectionManager] 连接参数:', params);

    // 对于DirectRoom模式，可以在这里设置一些特殊标记
    if (params.directRoom) {
      logger.info('[ConnectionManager] DirectRoom模式连接');
    }

    // 实际的连接逻辑由useConnectionManager中的connect方法调用useJoin执行
    // 这里只做状态管理和资源准备
  }

  /**
   * 执行实际的断开逻辑
   */
  private async performDisconnection(): Promise<void> {
    // 简化断开逻辑：如需断开RTC，请在调用侧执行 RtcClient.leaveRoom()
  }

  /**
   * 重连
   */
  async reconnect(params?: ConnectionParams): Promise<ConnectionResult> {
    logger.info('[ConnectionManager] 开始重连');

    // 先断开现有连接
    if (this.getState() === ConnectionState.CONNECTED) {
      await this.disconnect();
    }

    // 等待一段时间后重连
    await new Promise((resolve) => {
      setTimeout(resolve, this.config.retryDelay);
    });

    // 重新连接
    return this.connect(params);
  }

  /**
   * 获取当前状态
   */
  getState(): ConnectionState {
    return this.stateManager.getState();
  }

  /**
   * 监听状态变化
   */
  onStateChange(callback: StateChangeCallback): () => void {
    return this.stateManager.onStateChange(callback);
  }

  /**
   * 检查是否正在连接
   */
  isConnecting(): boolean {
    return this.getState() === ConnectionState.CONNECTING;
  }

  /**
   * 检查是否已连接
   */
  isConnected(): boolean {
    return this.getState() === ConnectionState.CONNECTED;
  }

  /**
   * 检查是否正在断开连接
   */
  isDisconnecting(): boolean {
    return this.getState() === ConnectionState.DISCONNECTING;
  }

  /**
   * 检查是否有错误
   */
  hasError(): boolean {
    return this.getState() === ConnectionState.ERROR;
  }

  /**
   * 获取最后一次错误
   */
  getLastError(): Error | null {
    const lastError = this.stateManager.getLastError();
    return lastError?.context?.error || null;
  }

  /**
   * 获取连接统计信息
   */
  getStats(): Record<string, any> {
    return {
      state: this.stateManager.getDebugInfo(),
      operations: this.operationGuard.getDebugInfo(),
      config: this.config,
    };
  }

  /**
   * 强制重置状态
   */
  forceReset(): void {
    logger.warn('[ConnectionManager] 强制重置状态');
    this.stateManager.forceReset();
    this.operationGuard.cancelAll();
  }

  /**
   * 销毁连接管理器
   */
  async destroy(): Promise<void> {
    if (this.isDestroyed) {
      logger.warn('[ConnectionManager] 已经销毁过了');
      return;
    }

    logger.info('[ConnectionManager] 销毁连接管理器');
    this.isDestroyed = true;

    // 取消所有正在进行的操作
    this.operationGuard.destroy();

    // 销毁状态管理器
    this.stateManager.destroy();

    logger.info('[ConnectionManager] 连接管理器已销毁');
  }

  /**
   * 检查是否已销毁
   */
  getDestroyedStatus(): boolean {
    return this.isDestroyed;
  }

  /**
   * 公有方法：执行受保护的操作
   */
  public executeOperation<T>(id: string, fn: () => Promise<T>, debounceMs = 0) {
    return this.operationGuard.execute(id, fn, debounceMs);
  }

  /**
   * 公有方法：状态转换
   */
  public transitionState(state: ConnectionState, context?: Record<string, any>) {
    return this.stateManager.transition(state, context);
  }
}
